import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Tooltip from '../../../common/Tooltip'
import { ReactComponent as InfoCircleIconDark } from '../../../../assets/images/common/icons/info-circle-icon-dark.svg'
import { setSelectedPaymentMethod } from '../../../../reducers/programRegistrationReducer'
import { displayWithCurrency } from '../../../../utility/currency'

import './PaymentMethodSelection.scss'

const PaymentMethodOption = ({ method, isSelected, isDisabled, onSelect }) => {
  const { t } = useTranslation()
  const course = useSelector(state => state.programPresentation.course)
  const selectedPackage = useSelector(state => state.programRegistration.selectedPackage)

  const price = displayWithCurrency(selectedPackage.price, course.community?.currency)
  const isOnline = method === 'card'

  return (
    <>
      <button
        type='button'
        disabled={isDisabled}
        onClick={() => onSelect(method)}
        className={classNames('payment-option-row', { 'payment-selected': isSelected })}
      >
        <span className='payment-radio'>
          {isSelected && <span className='payment-radio-dot' />}
        </span>
        <span className='payment-option-info'>
          <span className='payment-option-title'>
            {isOnline ? t('programs.registration.paymentMethod.online') : t('programs.registration.paymentMethod.offline')}
            {!isOnline && (
              <InfoCircleIconDark
                className='payment-info-icon'
                aria-hidden='true'
                data-tip={t('programs.registration.paymentMethod.cashInfoTooltip')}
                data-for='cashPaymentInfo'
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </span>
          <span className='payment-option-subtitle'>
            {isOnline
              ? t('programs.registration.paymentMethod.onlineSubtitle', { amount: price })
              : t('programs.registration.paymentMethod.offlineSubtitle', { amount: price })}
          </span>
        </span>
      </button>
      {!isOnline && <Tooltip id='cashPaymentInfo' />}
    </>
  )
}

PaymentMethodOption.propTypes = {
  method: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
}

const PaymentMethodSelection = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)

  const course = useSelector(state => state.programPresentation.course)
  const { selectedPaymentMethod, selectedPackage, enrollmentData, isProcessingPayment } = useSelector(state => state.programRegistration)

  useEffect(() => {
    if (!selectedPaymentMethod) {
      dispatch(setSelectedPaymentMethod(enrollmentData?.coursePaymentMethod.card ? 'card' : 'cash'))
    }
  }, [selectedPaymentMethod, enrollmentData, dispatch])

  const price = displayWithCurrency(selectedPackage.price, course.community?.currency)

  const handleSelectPaymentMethod = (paymentMethod) => {
    if (selectedPaymentMethod !== paymentMethod) {
      dispatch(setSelectedPaymentMethod(paymentMethod))
    }
    setIsOpen(false)
  }

  return (
    <div id='PaymentMethodSelection'>
      <h5 className='step-label mb-1'>{t('programs.registration.paymentMethod.selectLabel')}</h5>
      <p className='payment-method-subtitle'>{t('programs.registration.paymentMethod.selectSubtitle')}</p>
      <button type='button' className='payment-dropdown-toggle' disabled={isProcessingPayment} onClick={() => setIsOpen(!isOpen)}>
        <span className='payment-option-info'>
          <span className='payment-option-title'>
            {selectedPaymentMethod
              ? t(`programs.registration.paymentMethod.${selectedPaymentMethod === 'card' ? 'online' : 'offline'}`)
              : t('programs.registration.paymentMethod.choose')}
          </span>
          {selectedPaymentMethod && (
            <span className='payment-option-subtitle'>
              {selectedPaymentMethod === 'card'
                ? t('programs.registration.paymentMethod.onlineSubtitle', { amount: price })
                : t('programs.registration.paymentMethod.offlineSubtitle', { amount: price })}
            </span>
          )}
        </span>
        <i className={isOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'} aria-hidden='true' />
      </button>
      {isOpen && (
        <div className='payment-dropdown-menu'>
          {enrollmentData?.coursePaymentMethod.card && (
            <PaymentMethodOption
              method='card'
              isSelected={selectedPaymentMethod === 'card'}
              isDisabled={isProcessingPayment}
              onSelect={handleSelectPaymentMethod}
            />
          )}
          {enrollmentData?.coursePaymentMethod.cash && (
            <PaymentMethodOption
              method='cash'
              isSelected={selectedPaymentMethod === 'cash'}
              isDisabled={isProcessingPayment}
              onSelect={handleSelectPaymentMethod}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default PaymentMethodSelection
